import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import {
  ActionChip,
  ChevronLeftIcon,
  PageHeader,
  SectionCard,
} from '../components/layout/memberPrimitives';
import {
  TrackerSettingsRecord,
  DEFAULT_TRACKER_SETTINGS,
  TRACKER_LABELS,
  TrackerId,
} from '../types/dailyBite';
import {
  ALL_CORRELATION_IDS,
  CORRELATION_DESCRIPTIONS,
  CORRELATION_LABELS,
  CorrelationId,
  DEFAULT_CORRELATION_OPTINS,
} from '../types/correlations';
import {
  getTrackerSettings,
  getTrackerSummary,
  saveTrackerSettings,
} from '../services/dailyBiteService';
import {
  buildCheerUrl,
  createPartnerLink,
  listPartnerLinks,
  revokePartnerLink,
} from '../services/partnerService';
import { PARTNER_LABEL_MAX, PartnerLinkRecord } from '../types/partner';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const PRIMARY_TRACKERS: TrackerId[] = [
  'water',
  'protein',
  'caffeine',
  'alcohol',
  'nicotine',
  'mood',
  'gratitude',
  'digestion',
];

const TRACKER_DESCRIPTIONS: Record<TrackerId, string> = {
  water: 'Tap-to-add glasses. Shows your running total.',
  protein: 'Tap common servings (eggs, chicken, yogurt) — grams total automatically.',
  caffeine: 'Coffee, espresso, tea, matcha, energy drinks. Logs mg and time.',
  alcohol: 'UK units by drink type. Stays private unless you turn sharing on.',
  nicotine: 'Form-first: cigarettes, vape, pouch, gum, or patch. Off by default.',
  mood: '2D grid — energy × pleasant. Evening reflection.',
  gratitude: 'Rotating prompt each day. Private to you.',
  digestion: 'Bristol stool chart. Private by default.',
};

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative rounded-full transition"
      style={{
        width: 38,
        height: 22,
        backgroundColor: checked ? 'var(--melio-olive-deep)' : 'rgba(31, 42, 30, 0.18)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        className="absolute rounded-full bg-white transition"
        style={{
          width: 18,
          height: 18,
          top: 2,
          left: checked ? 18 : 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

export default function DailyBiteSettingsPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<TrackerSettingsRecord>(DEFAULT_TRACKER_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const loaded = await getTrackerSettings(currentUser.uid);
        setSettings(loaded);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const persist = async (next: TrackerSettingsRecord) => {
    if (!currentUser) return;
    setSettings(next);
    setSaving(true);
    try {
      await saveTrackerSettings(currentUser.uid, next);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const toggleOpt = (id: TrackerId, value: boolean) => {
    persist({
      ...settings,
      optedIn: { ...settings.optedIn, [id]: value },
    });
  };

  const toggleClinicianShare = (id: keyof TrackerSettingsRecord['sharing']['clinician'], value: boolean) => {
    persist({
      ...settings,
      sharing: {
        ...settings.sharing,
        clinician: { ...settings.sharing.clinician, [id]: value },
      },
    });
  };

  const toggleCorrelation = (id: CorrelationId, value: boolean) => {
    const current = { ...DEFAULT_CORRELATION_OPTINS, ...(settings.correlations || {}) };
    persist({
      ...settings,
      correlations: { ...current, [id]: value },
    });
  };

  const toggleReminder = (key: 'dailyAtHome' | 'eveningReflection', value: boolean) => {
    persist({
      ...settings,
      reminders: { ...settings.reminders, [key]: value },
    });
  };

  const setUnitSystem = (value: 'metric' | 'imperial') => {
    persist({ ...settings, unitSystem: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-10 melio-stagger">
      <PageHeader
        breadcrumb={[
          { label: 'Home', href: '/home' },
          { label: 'Daily Bite', href: '/today' },
          { label: 'Settings' },
        ]}
        eyebrow="Daily Bite"
        title="Customise your trackers"
        subtitle="Pick what you want to log, how it's measured, and who sees what. Everything is private unless you turn sharing on."
        rightSlot={
          <Link
            to="/today"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border melio-lift"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-ink)' }}
          >
            <ChevronLeftIcon size={14} /> Back to today
          </Link>
        }
      />

      <div className="mb-4 text-xs" style={{ color: 'var(--melio-text-muted)' }}>
        {saving ? 'Saving…' : savedAt ? 'Saved' : 'Changes save automatically.'}
      </div>

      <SectionCard eyebrow="Trackers" title="What you want to log" className="mb-6">
        <ul className="space-y-3">
          {PRIMARY_TRACKERS.map((id) => (
            <li key={id} className="flex items-start gap-4 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-base font-medium" style={{ color: 'var(--melio-ink)' }}>
                  {TRACKER_LABELS[id]}
                </p>
                <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                  {TRACKER_DESCRIPTIONS[id]}
                </p>
              </div>
              <Toggle
                checked={settings.optedIn[id]}
                onChange={(next) => toggleOpt(id, next)}
              />
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard eyebrow="Units" title="How you prefer to measure" className="mb-6">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setUnitSystem('metric')}
            className="rounded-full px-4 py-2 text-sm border"
            style={{
              backgroundColor: settings.unitSystem === 'metric' ? 'var(--melio-olive-deep)' : 'transparent',
              color: settings.unitSystem === 'metric' ? 'white' : 'var(--melio-ink)',
              borderColor: settings.unitSystem === 'metric' ? 'var(--melio-olive-deep)' : 'var(--melio-border)',
            }}
          >
            Metric (ml, g)
          </button>
          <button
            type="button"
            onClick={() => setUnitSystem('imperial')}
            className="rounded-full px-4 py-2 text-sm border"
            style={{
              backgroundColor: settings.unitSystem === 'imperial' ? 'var(--melio-olive-deep)' : 'transparent',
              color: settings.unitSystem === 'imperial' ? 'white' : 'var(--melio-ink)',
              borderColor: settings.unitSystem === 'imperial' ? 'var(--melio-olive-deep)' : 'var(--melio-border)',
            }}
          >
            Imperial (fl oz, oz)
          </button>
        </div>
      </SectionCard>

      <SectionCard
        eyebrow="Clinician sharing"
        title="What your care team sees in your quarterly report"
        className="mb-6"
      >
        <p className="text-xs mb-4" style={{ color: 'var(--melio-text-muted)' }}>
          Defaults follow how useful each tracker is clinically vs. how sensitive it is. Flip any switch at any time.
        </p>
        <ul className="space-y-3">
          {PRIMARY_TRACKERS.map((id) => (
            <li key={id} className="flex items-center gap-4 py-1">
              <div className="flex-1 text-sm">{TRACKER_LABELS[id]}</div>
              <Toggle
                checked={settings.sharing.clinician[id]}
                onChange={(next) => toggleClinicianShare(id, next)}
                disabled={!settings.optedIn[id]}
              />
            </li>
          ))}
          <li className="flex items-center gap-4 py-1 border-t mt-1 pt-3" style={{ borderColor: 'var(--melio-border)' }}>
            <div className="flex-1 text-sm">Weekly habit summary</div>
            <Toggle
              checked={settings.sharing.clinician.weeklyHabits}
              onChange={(next) => toggleClinicianShare('weeklyHabits', next)}
            />
          </li>
        </ul>
      </SectionCard>

      <SectionCard
        eyebrow="Correlation insights"
        title="Patterns we watch for in your own data"
        className="mb-6"
      >
        <p className="text-xs mb-2" style={{ color: 'var(--melio-text-muted)' }}>
          These surface on your Intelligence page once there are at least three weeks of data. They&rsquo;re observations, not claims &mdash; never causal, never compared to other people.
        </p>
        <p className="text-xs mb-4" style={{ color: 'var(--melio-text-muted)' }}>
          Output lives only here. Correlation cards are never shared with your clinician or a partner, regardless of your other sharing settings.
        </p>
        <ul className="space-y-3">
          {ALL_CORRELATION_IDS.map((id) => {
            const checked =
              (settings.correlations || DEFAULT_CORRELATION_OPTINS)[id] ?? DEFAULT_CORRELATION_OPTINS[id];
            return (
              <li key={id} className="flex items-start gap-4 py-1">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'var(--melio-ink)' }}>
                    {CORRELATION_LABELS[id]}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                    {CORRELATION_DESCRIPTIONS[id]}
                  </p>
                </div>
                <Toggle
                  checked={checked}
                  onChange={(next) => toggleCorrelation(id, next)}
                />
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <PartnerSection uid={currentUser?.uid || ''} />

      <SectionCard eyebrow="Reminders" title="When we nudge you" className="mb-6">
        <ul className="space-y-3">
          <li className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--melio-ink)' }}>
                Gentle reminder on home
              </p>
              <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                If you arrive on /home and haven&rsquo;t logged yet today, we show one inline nudge (never a modal, never twice a day).
              </p>
            </div>
            <Toggle
              checked={settings.reminders.dailyAtHome}
              onChange={(next) => toggleReminder('dailyAtHome', next)}
            />
          </li>
          <li className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--melio-ink)' }}>
                Evening reflection prompt
              </p>
              <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                Surfaces mood + gratitude after 18:00 local. (Visual-only for now; notifications come with phase 2.)
              </p>
            </div>
            <Toggle
              checked={settings.reminders.eveningReflection}
              onChange={(next) => toggleReminder('eveningReflection', next)}
            />
          </li>
        </ul>
      </SectionCard>

      <div className="mt-6 flex flex-wrap gap-2">
        <ActionChip label="Back to today" href="/today" />
        <ActionChip label="Data export — coming soon" href="/today/settings" />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Partner section — invite, list, and revoke partner cheerlead links.
   Owns its own state because the source of truth is the `partner_links`
   subcollection, not the tracker_settings doc.
   ──────────────────────────────────────────────────────────────────────── */

function PartnerSection({ uid }: { uid: string }) {
  const [links, setLinks] = useState<PartnerLinkRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<PartnerLinkRecord | null>(null);

  useEffect(() => {
    if (!uid) return;
    let active = true;
    (async () => {
      try {
        const list = await listPartnerLinks(uid);
        if (active) setLinks(list);
      } catch (err) {
        console.error('listPartnerLinks failed', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [uid]);

  const handleCreate = async (label: string) => {
    if (!uid) return;
    setCreating(true);
    setError(null);
    try {
      const userSnap = await getDoc(doc(db, 'users', uid));
      const userData = userSnap.exists() ? (userSnap.data() as Record<string, any>) : {};
      const memberFirstName =
        userData.firstName || (userData.displayName || '').split(/\s+/)[0] || 'a friend';

      const summary = await getTrackerSummary(uid);
      const link = await createPartnerLink(uid, {
        label,
        memberFirstName,
        summary,
      });
      setLinks((prev) => [link, ...prev]);
      setJustCreated(link);
      setShowInviteModal(false);
    } catch (err: any) {
      console.error('createPartnerLink failed', err);
      setError(err?.message || 'Could not create the link. Try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (linkId: string) => {
    if (!uid) return;
    if (!window.confirm('Revoke this partner link? They will no longer see your streak.')) return;
    try {
      await revokePartnerLink(uid, linkId);
      setLinks((prev) =>
        prev.map((l) => (l.id === linkId ? { ...l, status: 'revoked' as const } : l)),
      );
    } catch (err) {
      console.error('revokePartnerLink failed', err);
      setError('Could not revoke. Try again.');
    }
  };

  const activeLinks = links.filter((l) => l.status === 'active');

  return (
    <SectionCard
      eyebrow="Partner mode"
      title="Let one person cheerlead — never see your data"
      className="mb-6"
    >
      <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
        Share a private link with a partner, friend or family member. They’ll see only your streak
        and badge count — never the trackers themselves — and can send short notes that land on
        your Daily Bite. You can revoke any link any time.
      </p>

      {loading ? (
        <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
          Loading…
        </p>
      ) : (
        <>
          {activeLinks.length === 0 ? (
            <p className="text-xs mb-4" style={{ color: 'var(--melio-text-muted)' }}>
              No partners invited yet.
            </p>
          ) : (
            <ul className="space-y-2 mb-4">
              {activeLinks.map((link) => (
                <li
                  key={link.id}
                  className="rounded-2xl border p-3 flex items-center gap-3 flex-wrap"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: 'var(--melio-ink)' }}>
                      {link.label}
                    </p>
                    <p className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>
                      {link.cheerleadCount > 0
                        ? `${link.cheerleadCount} cheerlead${link.cheerleadCount === 1 ? '' : 's'} so far`
                        : 'No cheerleads yet'}
                    </p>
                  </div>
                  <CopyLinkButton url={buildCheerUrl(link.id)} compact />
                  <button
                    type="button"
                    onClick={() => handleRevoke(link.id)}
                    className="text-xs px-3 py-1.5 rounded-full"
                    style={{ color: 'var(--melio-terracotta-deep)' }}
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => {
              setShowInviteModal(true);
              setJustCreated(null);
            }}
            className="inline-flex items-center rounded-full px-4 py-2 text-sm melio-lift"
            style={{ backgroundColor: 'var(--melio-olive-deep)', color: 'white' }}
          >
            Invite a partner
          </button>

          {error ? (
            <p className="text-xs mt-3" style={{ color: 'var(--melio-terracotta-deep)' }}>
              {error}
            </p>
          ) : null}
        </>
      )}

      {showInviteModal ? (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onCreate={handleCreate}
          creating={creating}
        />
      ) : null}

      {justCreated ? (
        <ShareLinkModal
          link={justCreated}
          onClose={() => setJustCreated(null)}
        />
      ) : null}
    </SectionCard>
  );
}

function InviteModal({
  onClose,
  onCreate,
  creating,
}: {
  onClose: () => void;
  onCreate: (label: string) => Promise<void> | void;
  creating: boolean;
}) {
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Give the link a name so you can find it later.');
      return;
    }
    setError(null);
    await onCreate(trimmed);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(31, 42, 30, 0.45)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-3xl border p-6"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <p
          className="text-[10px] uppercase tracking-[0.22em] mb-2"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          Partner mode
        </p>
        <h3
          className="text-xl mb-1"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          Invite a partner
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--melio-text-muted)' }}>
          We’ll generate a private link you can send them. They open it in any browser — no app
          install, no Melio account.
        </p>

        <label className="block text-xs mb-1" style={{ color: 'var(--melio-text-muted)' }}>
          Who is this for?
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Sarah (wife)"
          autoFocus
          maxLength={PARTNER_LABEL_MAX}
          className="w-full rounded-xl border px-3 py-2 text-sm mb-4"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        />

        {error ? (
          <p className="text-xs mb-3" style={{ color: 'var(--melio-terracotta-deep)' }}>
            {error}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-full px-4 py-2 text-sm"
            style={{ color: 'var(--melio-text-muted)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={creating}
            className="inline-flex items-center rounded-full px-4 py-2 text-sm melio-lift"
            style={{
              backgroundColor: 'var(--melio-olive-deep)',
              color: 'white',
              opacity: creating ? 0.6 : 1,
            }}
          >
            {creating ? 'Creating…' : 'Generate link'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareLinkModal({
  link,
  onClose,
}: {
  link: PartnerLinkRecord;
  onClose: () => void;
}) {
  const url = buildCheerUrl(link.id);

  const handleShare = async () => {
    const nav = navigator as any;
    if (typeof nav?.share === 'function') {
      try {
        await nav.share({
          title: 'Cheer me on',
          text: `Help cheer me on while I build a daily health habit on Melio: ${url}`,
        });
      } catch {
        /* user cancelled */
      }
    }
  };

  const canShare = typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(31, 42, 30, 0.45)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-3xl border p-6"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <p
          className="text-[10px] uppercase tracking-[0.22em] mb-2"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          Link ready
        </p>
        <h3
          className="text-xl mb-1"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          Share this with {link.label}
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--melio-text-muted)' }}>
          Send the link however you usually message them. They’ll only see your streak and badge
          count — no tracker data ever.
        </p>

        <div
          className="rounded-xl border px-3 py-2 mb-4 text-[11px] break-all"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-ink)' }}
        >
          {url}
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap">
          {canShare ? (
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center rounded-full px-4 py-2 text-sm border"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-ink)' }}
            >
              Share via…
            </button>
          ) : null}
          <CopyLinkButton url={url} />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-full px-4 py-2 text-sm"
            style={{ color: 'var(--melio-text-muted)' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyLinkButton({ url, compact }: { url: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center rounded-full px-4 py-2 text-sm"
      style={{
        backgroundColor: copied ? 'rgba(79, 93, 24, 0.18)' : 'var(--melio-olive-deep)',
        color: copied ? 'var(--melio-olive-deep)' : 'white',
      }}
    >
      {copied ? 'Copied' : compact ? 'Copy link' : 'Copy link'}
    </button>
  );
}
